import $ from 'jQuery';
import React from 'react';
import machine_app_implementation_loader from '../models/machine_app_implementation_loader';
import machine_app_state from '../models/machine_app_state';
import { gMachineAppViews } from '../machine_app_global_variables';
import { machineAppApplicationLandingPage } from './machine_app_application_view';
import { renderPrerequisiteView } from './machine_app_prerequisite_view';
import { prerequisiteValidator } from '../machine_app_prerequisite_validation';
import { getEmptyView } from './machine_app_empty_view';
import { getMachineParametersView } from './machine_app_parameters_view';
import { getItemListView } from './machine_app_item_list_view';
import { MachineAppComponentWrapper } from '../components/MachineAppComponentWrapper';
import { Footer } from '../components/Footer';
import { isStepValid, validateGlobalAssetItem } from '../machine_app_view_validation';
import environment from "@vse/environment";

/**
 * @typedef {Object} MachineAppItemViewProps
 * @property {any} machineAppLibrary
 * @property {import('./machine_app_view').MachineAppViewString} view
 * @property {String} machineAppDefinitionName
 * @property {Object.<string, Function>} globalAssetCreatorMap
 */
/**
 * Renders an editor for a specific MachineAppView (machineParameters, globalAsset, configuration, or application).
 *
 * @param {MachineAppItemViewProps} MachineAppItemViewProps Contains all of the information we need to display a view.
 *
 * @returns {JQuery}
 */
export function machineAppItemView({
  machineAppLibrary,
  view,
  updateLeftPane,
  machineAppDefinitionName,
  globalAssetCreatorMap,
  openView,
  orderedViews,
}) {
  const container = $('<div>').attr('id', 'machineapp_container');
  const header = $('<div>').attr('id', 'machineapp_header').addClass('machineapp_editor_header');
  const body = $('<div>').attr('id', 'machineapp_content');
  const footer = $('<div>').attr('id', 'machineapp_footer').addClass('machineapp_editor_footer');
  const machineAppType = machineAppLibrary.getType();
  const prerequisiteValidation = prerequisiteValidator(machineAppType);
  const validators = machine_app_implementation_loader.instance.getValidators(machineAppType);
  const flavorText = machine_app_implementation_loader.instance.getFlavorText(machineAppType);
  const previewUiHooks = machine_app_implementation_loader.instance.getOptionalUiHooks(machineAppLibrary.getType()).preview ?? {};

  const itemViewProps = {
    machineAppLibrary,
    onUpdate: () => { updateLeftPane(); lRender(); },
    createNewItem: undefined,
    removeItem: undefined,
    listItems: undefined,
    flavorText: undefined,
    getPreview: undefined,
    isItemValidated: undefined,
    view: undefined,
    assetType: undefined
  };

  const lRender = function () {
    let viewContents = {
      body: undefined,
      header: undefined,
    };

    const viewList = Object.values(gMachineAppViews);
    const isCurrentStepValid = isStepValid(view, viewList, machineAppLibrary);
    const nextButtonOnClick = () => {openView(orderedViews[orderedViews.indexOf(view) + 1]);};
    const backButtonOnClick = () => {openView(orderedViews[orderedViews.indexOf(view) - 1]);};
    let onlyShowNext = false;

    let nextProps = {
      buttonType: 'next',
      onClick: nextButtonOnClick,
      disabled: !isCurrentStepValid,
    };

    let backProps = {
      buttonType: 'back',
      onClick: backButtonOnClick,
      disabled: false
    };

    let tipProps = undefined;

    if (orderedViews.indexOf(view) === 0) {
      backProps = undefined;
    }

    if (orderedViews.indexOf(view) === orderedViews.length - 1) {
      nextProps = undefined;
    }

    if (prerequisiteValidation.success) {
      switch (view) {
        case gMachineAppViews.machineParameters:
          itemViewProps.createNewItem = (name) => machineAppLibrary.newCalibration(name);
          itemViewProps.removeItem = (id, onSuccess, onError) => machineAppLibrary.removeCalibration(id, onSuccess, onError);
          itemViewProps.listItems = () => machineAppLibrary.getMachineParameters();
          itemViewProps.flavorText = flavorText.machineParameters;
          itemViewProps.getPreview = previewUiHooks.machineParameters ? (item, onEdit) => getMachineParametersPreview(previewUiHooks.machineParameters, item, onEdit) : undefined;
          //TODO: for now use first key in globalAssetMap,
          //but we need to consider for later what to do if we have multiple assets to define
          itemViewProps.globalAssetFlavorText = globalAssetCreatorMap ? getGlobalAssetFlavorText(flavorText.globalAssetFlavorTextMap, Object.keys(globalAssetCreatorMap)[0]) : undefined;
          itemViewProps.view = view;
          if(!itemViewProps.listItems()){
            viewContents = getEmptyView(
              itemViewProps.onUpdate,
              itemViewProps.createNewItem,
              itemViewProps.listItems,
              itemViewProps.flavorText,
              itemViewProps.view,
            );
          }
          else {
            viewContents = getMachineParametersView(
              itemViewProps.machineAppLibrary,
              itemViewProps.onUpdate,
              itemViewProps.listItems,
              itemViewProps.flavorText,
              itemViewProps.getPreview,
            );
            onlyShowNext = true;
          }
          break;
        case gMachineAppViews.globalAsset:
        {
          //TODO: for now use first key in globalAssetMap,
          //but we need to consider for later what to do if we have multiple assets to define
          const assetType = Object.keys(globalAssetCreatorMap)[0];
          itemViewProps.createNewItem = (name, assetType) => machineAppLibrary.newGlobalAsset(name, assetType);
          itemViewProps.removeItem = (id, onSuccess, onError) => machineAppLibrary.removeGlobalAsset(id, onSuccess, onError);
          itemViewProps.listItems = (assetType) => machineAppLibrary.listGlobalAssetsByAssetType(assetType);
          itemViewProps.flavorText = getGlobalAssetFlavorText(flavorText.globalAssetFlavorTextMap, assetType);
          itemViewProps.getPreview = previewUiHooks.globalAssetPreviewMap?.[assetType] ? (item) => getGlobalAssetPreview(previewUiHooks.globalAssetPreviewMap[assetType], item ) : undefined;
          itemViewProps.isItemValidated = (item) => validateGlobalAssetItem(validators.globalAssetValidatorMap, assetType, item);
          itemViewProps.view = view;
          itemViewProps.assetType = assetType;
          if (itemViewProps.listItems(assetType).length == 0) {
            viewContents = getEmptyView(
              itemViewProps.onUpdate,
              itemViewProps.createNewItem,
              itemViewProps.listItems,
              itemViewProps.flavorText,
              itemViewProps.view,
              itemViewProps.assetType
            );
          }
          else {
            viewContents = getItemListView(itemViewProps);
          }
          break;
        }
        case gMachineAppViews.configuration:
          itemViewProps.createNewItem = (name) => machineAppLibrary.newConfiguration(name);
          itemViewProps.removeItem = (id, onSuccess, onError) => machineAppLibrary.removeConfiguration(id, onSuccess, onError);
          itemViewProps.listItems = () => machineAppLibrary.listConfigurations();
          itemViewProps.flavorText = flavorText.configuration;
          itemViewProps.getPreview = previewUiHooks.configuration ? (item) => getConfigurationPreview(previewUiHooks.configuration, item) : undefined;
          itemViewProps.isItemValidated = (item) => validators.configuration(item).success;
          itemViewProps.view = view;
          if (itemViewProps.listItems().length == 0) {
            viewContents = getEmptyView(
              itemViewProps.onUpdate,
              itemViewProps.createNewItem,
              itemViewProps.listItems,
              itemViewProps.flavorText,
              itemViewProps.view,
            );
          }
          else {
            viewContents = getItemListView(itemViewProps);
          }
          break;
        case gMachineAppViews.application:{
          header.hide();
          const addApplicationTip = () => {
            let applicationTipText;
            if(environment.isOnPendant()){
              applicationTipText = 'Continue to App Launcher Step via the Navigation Bar to Play an Application Instance';
            }
            else if (environment.isOnCad()) {
              applicationTipText = 'Continue to "Step 3: Operate" to Play an Application Instance';
            }

            if(machineAppLibrary.listApplications().length != 0) {
              tipProps = {
                tipText: applicationTipText,
                tipType: 'tip'
              };
            }
          };
          const onUpdateApplication = () => {
            itemViewProps.onUpdate();
            addApplicationTip();
          };
          viewContents = machineAppApplicationLandingPage(machineAppLibrary, onUpdateApplication, machineAppDefinitionName);
          addApplicationTip();
          break;
        }
        default:
          console.error('Unknown tab: ' + view);
          break;
      }

      const footerContainer = MachineAppComponentWrapper(
        <Footer
          nextButton={nextProps}
          backButton={backProps}
          tipProps={tipProps}
          options={{removeUndefinedElements: onlyShowNext}}
        />
      );

      header.empty().append(viewContents.header);
      body.empty().append(viewContents.body);
      footer.empty().append(
        footerContainer
      );

    }
    else {
      header.hide();
      body.empty().append(renderPrerequisiteView(machineAppLibrary));
      footer.empty();
    }
  };

  container.append(
    header,
    body,
    footer
  );

  lRender();
  return container;
}

function getMachineParametersPreview(machineParameterPreviewHook, item, onEdit) {
  return machineParameterPreviewHook({
    machineParameters: item.getData(),
    machineAppInterface: machine_app_state.getInterface(),
    editStep: (defaultStepId) => { onEdit(defaultStepId); }
  });
}

function getGlobalAssetPreview(globalAssetPreviewHook, item) {
  return globalAssetPreviewHook({
    globalAsset: item.getData(),
    machineAppInterface: machine_app_state.getInterface()
  });
}

function getConfigurationPreview(configurationPreviewHook, item) {
  return configurationPreviewHook({
    configuration: item.getData(),
    machineAppInterface: machine_app_state.getInterface(),
  });
}

function getGlobalAssetFlavorText(flavorTextMap, assetType) {
  const foundKey = Object.keys(flavorTextMap).find(asset => asset === assetType);
  if(!foundKey) {
    return flavorTextMap.default;
  }
  return flavorTextMap[foundKey];
}

